/* eslint-disable camelcase */

import store from '@/store/store';
import { parseBoolean, parseString, parseNumber, parseBetween } from '@/utils/sanitaze.utils';
import { consoleError } from '@/utils/console.utils';
import { hasProp } from '@/utils/validate.utils';
import { TAI, DEVICES_SHORT, TEMP_SENSOR, IAQ } from '@/app.config';
import { isArray } from 'util';

/**
 * @typedef {Object} Device
 * @property {Boolean} acs_available - Indica si existe Agua Caliente Sanitaria
 * @property {Number} acs_temp - Temperatura del Agua Caliente Sanitaria
 * @property {Boolean} aidooit - Indica si un webserver pro
 * @property {Number} aqmode - Indica el modo de ionización
 * @property {Boolean} aqpresent - Indica que hay módulo de calidad de aire
 * @property {Boolean} aqready - Indica que hay comunicación con el módulo de calidad de aire
 * @property {Number} aqthrlow - Indica el umbral inferior nivel IAQ
 * @property {Number} aqthrhigh - Indica el umbral superior del IAQ
 * @property {Number} aqpm1_0 - Densidad de partículas 1.0 pm
 * @property {Number} aqpm2_5 - Densidad de partículas 2.5 pm
 * @property {Number} aqpm10 - Densidad de partículas 10 pm
 * @property {Boolean} aqionstatus0 - Estado on/off ionizador 0
 * @property {Boolean} aqionstatus1 - Estado on/off ionizador 1
 * @property {Boolean} aqionstatus2 - Estado on/off ionizador 2
 * @property {Boolean} aqionstatus3 - Estado on/off ionizador 3
 * @property {Boolean} aqionstatus4 - Estado on/off ionizador 4
 * @property {Boolean} aqionstatus5 - Estado on/off ionizador 5
 * @property {Boolean} aqionstatus6 - Estado on/off ionizador 6
 * @property {Boolean} aqionstatus7 - Estado on/off ionizador 7
 * @property {Boolean} block_on - Permite modificar el encendido
 * @property {Boolean} block_onoff - Permite modificar el encendido/apagado
 * @property {Boolean} block_mode - Permite modificar el modo
 * @property {Boolean} block_setpoint - Permite modificar la temperatura
 * @property {Boolean} block_autotemp - Permite modificar la temperatura en modo auto
 * @property {Boolean} block_autospeed - Permite modificar la velocidad en modo auto
 * @property {Boolean} block_drytemp - Permite modificar la temperatura en modo deshumedecer
 * @property {Boolean} block_dryspeed - Permite modificar la velocidad en modo deshumedecer
 * @property {Boolean} block_fantemp - Permite modificar la temperatura en modo ventilación
 * @property {Number} consumption_ue - Consumo (A * 10)
 * @property {String} error_ascii1
 * @property {String} error_ascii2
 * @property {Number} error_value
 * @property {Number} ext_temp
 * @property {String} icon - Nombre del icono del dispositivo
 * @property {Boolean} isConnected
 * @property {Boolean} isS21 - Indica si la unidad es S21
 * @property {Number} local_temp - Indica la temperatura ambiente de la zona
 * @property {Boolean} leds_off - Indica los leds del webserver se encienden o no
 * @property {String} link_manufacturer - Indica el nombre del fabricante de un dispositivo vinculado
 * @property {String} link_deviceID - Indica el ID del dispositivo vinculado
 * @property {String} link_name_thermostat - Indica el nombre del termostato del dispositivo vinculado
 * @property {Boolean} machineready
 * @property {Number} manufacturerID - ID del fabricante
 * @property {String} manufacturer - Nombre del fabricante
 * @property {Boolean} master - Indica si el termostato está configurado como maestro
 * @property {Number} mode - Indica el modo seleccionado
 * @property {Array} mode_available - Listado con los modos disponibles
 * @property {String} name - Nombre de la zona
 * @property {Boolean} power - Indica si la unidad está encendida produciendo aire
 * @property {Boolean} power_acs - Indica si la unidad está encendida produciendo Agua Caliente Sanitaria
 * @property {Boolean} power_hot_water - Indica si la unidad está produciendo ACS en modo powerful
 * @property {Number} pspeed
 * @property {Number} exch_heat_temp_iu - Temperatura del intercambiador de calor de la unidad interior (ºC/ºF *10)
 * @property {Number} gas_pipe_temp_iu - Temperatura de la tubería de gas de la unidad interior (ºC/ºF *10)
 * @property {Number} exch_heat_temp_ue - Temperatura del intercambiador de calor de la unidad exterior interior (ºC/ºF *10)
 * @property {Number} disch_comp_temp_ue - Temperatura de descarga del compresor unidad exterior (ºC/ºF *10)
 * @property {Number} exp_valv_ue - Válvula de expansión de la unidad exterior (ºC/ºF *10)
 * @property {Number} exp_valv_ui - Válvula de expansión de la unidad interior (ºC/ºF *10)
 * @property {Number} pe_ue - Presión de evaporación (MPa*100)
 * @property {Number} pc_ue - Presión de condensación (MPa*100)
 * @property {Number} range_sp_acs_max - temperatura máxima del agua caliente sanitaria
 * @property {Number} range_sp_auto_air_max - temperatura máxima en modo auto con unidades de aire
 * @property {Number} range_sp_cool_air_max - temperatura máxima en modo frío con unidades de aire
 * @property {Number} range_sp_hot_air_max - temperatura máxima en modo calor con unidades de aire
 * @property {Number} range_sp_vent_air_max - temperatura máxima en modo ventilación con unidades de aire
 * @property {Number} range_sp_dry_air_max - temperatura máxima en modo seco con unidades de aire
 * @property {Number} range_sp_auto_water_max - temperatura máxima en modo auto con unidades de agua
 * @property {Number} range_sp_cool_water_max - temperatura máxima en modo frío con unidades de agua
 * @property {Number} range_sp_hot_water_max - temperatura máxima en modo calor con unidades de agua
 * @property {Number} range_sp_acs_min - temperatura mínima del agua caliente sanitaria
 * @property {Number} range_sp_auto_air_min - temperatura mínima en modo auto con unidades de aire
 * @property {Number} range_sp_cool_air_min - temperatura mínima en modo frío con unidades de aire
 * @property {Number} range_sp_hot_air_min - temperatura mínima en modo calor con unidades de aire
 * @property {Number} range_sp_vent_air_min - temperatura mínima en modo ventilación con unidades de aire
 * @property {Number} range_sp_dry_air_min - temperatura mínima en modo seco con unidades de aire
 * @property {Number} range_sp_auto_water_min - temperatura mínima en modo auto con unidades de agua
 * @property {Number} range_sp_cool_water_min - temperatura mínima en modo frío con unidades de agua
 * @property {Number} range_sp_hot_water_min - temperatura mínima en modo calor con unidades de agua
 * @property {Number} real_mode - Cuando el modo es auto, indica si es auto calor o auto frio
 * @property {Number} setpoint_acs - temperatura seleccionada del agua caliente sanitaria
 * @property {Number} setpoint_air_auto - temperatura seleccionada en modo auto con unidades de aire
 * @property {Number} setpoint_air_cool - temperatura seleccionada en modo frío con unidades de aire
 * @property {Number} setpoint_air_heat - temperatura seleccionada en modo calor con unidades de aire
 * @property {Number} setpoint_air_vent - temperatura seleccionada en modo ventilación con unidades de aire
 * @property {Number} setpoint_air_dry - temperatura seleccionada en modo seco con unidades de aire
 * @property {Number} setpoint_water_auto - temperatura seleccionada en modo auto con unidades de agua
 * @property {Number} setpoint_water_cool - temperatura seleccionada en modo frío con unidades de agua
 * @property {Number} setpoint_water_heat - temperatura seleccionada en modo calor con unidades de agua
 * @property {Boolean} setpoint_step - Indica si el fabricante trabaja con un paso de 0.5º
 * @property {Boolean} slats_autoud - modo auto en lamas verticales (ud - updown)
 * @property {Boolean} slats_autolr - Indica si las lamas horizontales tienen modo auto (lr - leftright)
 * @property {Boolean} slats_swingud - Indica si las lamas verticales tienen modo swing (ud - updown)
 * @property {Boolean} slats_swinglr - Indica si las lamas horizontales tienen modo swing (lr - leftright)
 * @property {Number} slats_hnum - Indica el número de posiciones disponibles en las lamas horizontales
 * @property {Number} slats_vnum - Indica el número de posiciones disponibles en las lamas verticales
 * @property {Number} slats_horizontal_1 - Indica la posición seleccionada en las lamas horizontales
 * @property {Number} slats_vertical_1 - Indica la posición seleccionada en las lamas verticales
 * @property {Array} speed_available - Listado con las velocidades disponibles
 * @property {Number} speed_state - Velocidad seleccionada
 * @property {Number} stat_channel
 * @property {Number} stat_rssi
 * @property {String} stat_ssid
 * @property {Boolean} tai_th - Indica si la máquina está trabajando como TAI o como TH
 * @property {String} timezoneId - Indica la zona horaria del grupo donde está la unidad
 * @property {Boolean} tsensor_error - Indica si existe un error con el sensor de temperatura en la zona
 * @property {Number} units - Indica la unidad de temperatura utiizada ºC/ºF
 * @property {String} version - Indica la versión del webserver
 * @property {Number} water_input_temp - Indica la temperatura de entrada del agua
 * @property {Number} water_output_temp - Indica la temperatura de salida del agua
 * @property {Number} work_temp - Indica la temperatura ambiente de la zona
 * @property {Number} work_temp_selec_sensor - Indica si la medición ambiente es la de trabajo, la del termostato o una mezcla de las dos
 */

/**
 * Da formato a los datos de un dispositivo
 *
 * @param {String} mac - Mac del dispositivo
 * @param {Object} deviceData - Datos del dispositivo del backend
 * @return {Device} - Datos formateados de un dispositivo
 */
// eslint-disable-next-line consistent-return
const sanitizeDevice = deviceData => {
  try {
    const device = {};

    // Get device data and sanitize it
    if (hasProp(deviceData, 'acs_available')) {
      device.acs_available = parseBoolean(deviceData.acs_available);
    }
    if (hasProp(deviceData, 'acs_temp')) {
      device.acs_temp = parseNumber(deviceData.acs_temp);
    }
    if (hasProp(deviceData, 'aidooit')) {
      device.pro = parseBoolean(deviceData.aidooit);
    }
    if (hasProp(deviceData, 'aqmode')) {
      device.aqmode = parseNumber(deviceData.aqmode);
    }
    if (hasProp(deviceData, 'aqpresent')) {
      device.aqpresent = parseBoolean(deviceData.aqpresent);
    }
    if (hasProp(deviceData, 'aqready')) {
      device.aqready = parseBoolean(deviceData.aqready);
    }
    if (hasProp(deviceData, 'aqthrlow')) {
      let low = parseNumber(deviceData.aqthrlow);
      low = parseBetween(low, IAQ.MAX, IAQ.MIN);
      device.aqthrlow = low;
    }
    if (hasProp(deviceData, 'aqthrhigh')) {
      let high = parseNumber(deviceData.aqthrhigh);
      high = parseBetween(high, IAQ.MAX, IAQ.MIN);
      device.aqthrhigh = high;
    }
    if (hasProp(deviceData, 'aqpm1_0')) {
      device.aqpm1_0 = parseNumber(deviceData.aqpm1_0);
    }
    if (hasProp(deviceData, 'aqpm2_5')) {
      device.aqpm2_5 = parseNumber(deviceData.aqpm2_5);
    }
    if (hasProp(deviceData, 'aqpm10')) {
      device.aqpm10 = parseNumber(deviceData.aqpm10);
    }
    if (hasProp(deviceData, 'aqionstatus0')) {
      device.aqionstatus0 = parseBoolean(deviceData.aqionstatus0);
    }
    if (hasProp(deviceData, 'aqionstatus1')) {
      device.aqionstatus1 = parseBoolean(deviceData.aqionstatus1);
    }
    if (hasProp(deviceData, 'aqionstatus2')) {
      device.aqionstatus2 = parseBoolean(deviceData.aqionstatus2);
    }
    if (hasProp(deviceData, 'aqionstatus3')) {
      device.aqionstatus3 = parseBoolean(deviceData.aqionstatus3);
    }
    if (hasProp(deviceData, 'aqionstatus4')) {
      device.aqionstatus4 = parseBoolean(deviceData.aqionstatus4);
    }
    if (hasProp(deviceData, 'aqionstatus5')) {
      device.aqionstatus5 = parseBoolean(deviceData.aqionstatus5);
    }
    if (hasProp(deviceData, 'aqionstatus6')) {
      device.aqionstatus6 = parseBoolean(deviceData.aqionstatus6);
    }
    if (hasProp(deviceData, 'aqionstatus7')) {
      device.aqionstatus7 = parseBoolean(deviceData.aqionstatus7);
    }
    if (hasProp(deviceData, 'block_on')) {
      device.block_on = parseBoolean(deviceData.block_on);
    }
    if (hasProp(deviceData, 'block_off')) {
      device.block_off = parseBoolean(deviceData.block_off);
    }
    if (hasProp(deviceData, 'block_mode')) {
      device.block_mode = parseBoolean(deviceData.block_mode);
    }
    if (hasProp(deviceData, 'block_setpoint')) {
      device.block_setpoint = parseBoolean(deviceData.block_setpoint);
    }
    if (hasProp(deviceData, 'consumption_ue')) {
      device.consumption_ue = parseNumber(deviceData.consumption_ue);
    }
    if (hasProp(deviceData, 'device_master_slave')) {
      device.device_master_slave = parseBoolean(deviceData.device_master_slave);
    }
    if (hasProp(deviceData, 'error_ascii1')) {
      device.error_ascii1 = parseString(deviceData.error_ascii1);
    }
    if (hasProp(deviceData, 'error_ascii2')) {
      device.error_ascii2 = parseString(deviceData.error_ascii2);
    }
    if (hasProp(deviceData, 'error_value')) {
      device.error_value = parseNumber(deviceData.error_value);
    }
    if (hasProp(deviceData, 'ext_temp')) {
      device.ext_temp = parseNumber(deviceData.ext_temp);
    }
    if (hasProp(deviceData, 'icon')) {
      device.icon = parseString(deviceData.icon);
    }
    if (hasProp(deviceData, 'isConnected')) {
      device.isConnected = parseBoolean(deviceData.isConnected);
    }
    if (hasProp(deviceData, 'manufacturer') && hasProp(deviceData.manufacturer, '_id')) {
      device.isS21 = parseNumber(deviceData.manufacturer._id) === 16;
    }
    if (hasProp(deviceData, 'local_temp')) {
      device.local_temp = parseNumber(deviceData.local_temp);
    }
    if (hasProp(deviceData, 'leds_off')) {
      device.leds_off = parseBoolean(deviceData.leds_off);
    }
    if (hasProp(deviceData, 'link_t')) {
      if (hasProp(deviceData.link_t, 'manufacturer')) {
        const manufacturer = parseString(deviceData.link_t.manufacturer);
        device.link_manufacturer = DEVICES_SHORT[manufacturer];
      }
      if (hasProp(deviceData.link_t, '_id')) {
        device.link_deviceID = parseString(deviceData.link_t._id);
      }
      if (hasProp(deviceData.link_t, 'isConnected')) {
        device.link_isConnected = parseBoolean(deviceData.link_t.isConnected);
      }
      if (hasProp(deviceData.link_t, 'name')) {
        device.link_name_thermostat = parseString(deviceData.link_t.name);
      }
    }
    if (hasProp(deviceData, 'fallback')) {
      device.link_fallback = parseBoolean(deviceData.fallback);
    }
    if (hasProp(deviceData, 'machineready')) {
      device.machineready = parseBoolean(deviceData.machineready);
    }
    if (hasProp(deviceData, 'manufacturerID')) {
      device.manufacturerID =
        deviceData.manufacturer && deviceData.manufacturer._id
          ? parseNumber(deviceData.manufacturer._id)
          : null;
    }
    if (hasProp(deviceData, 'manufacturer')) {
      device.manufacturer =
        deviceData.manufacturer && deviceData.manufacturer.text
          ? parseString(deviceData.manufacturer.text)
          : null;
    }
    if (hasProp(deviceData, 'master')) {
      device.master = parseBoolean(deviceData.master);
    }
    if (hasProp(deviceData, 'mode_available')) {
      device.mode_available =
        deviceData.mode_available && deviceData.mode_available.length
          ? deviceData.mode_available
          : null;
    }
    if (hasProp(deviceData, 'mode')) {
      device.mode = parseNumber(deviceData.mode);
    }
    if (hasProp(deviceData, 'name')) {
      device.name = parseString(deviceData.name);
    }
    if (hasProp(deviceData, 'power')) {
      device.power = parseBoolean(deviceData.power);
    }
    if (hasProp(deviceData, 'power_acs')) {
      device.power_acs = parseBoolean(deviceData.power_acs);
    }
    if (hasProp(deviceData, 'power_hot_water')) {
      device.power_hot_water = parseBoolean(deviceData.power_hot_water);
    }
    if (hasProp(deviceData, 'pspeed')) {
      device.pspeed = parseNumber(deviceData.pspeed);
    }
    if (hasProp(deviceData, 'exch_heat_temp_iu')) {
      device.exch_heat_temp_iu = parseNumber(deviceData.exch_heat_temp_iu);
    }
    if (hasProp(deviceData, 'gas_pipe_temp_iu')) {
      device.gas_pipe_temp_iu = parseNumber(deviceData.gas_pipe_temp_iu);
    }
    if (hasProp(deviceData, 'exch_heat_temp_ue')) {
      device.exch_heat_temp_ue = parseNumber(deviceData.exch_heat_temp_ue);
    }
    if (hasProp(deviceData, 'disch_comp_temp_ue')) {
      device.disch_comp_temp_ue = parseNumber(deviceData.disch_comp_temp_ue);
    }
    if (hasProp(deviceData, 'exp_valv_ue')) {
      device.exp_valv_ue = parseNumber(deviceData.exp_valv_ue);
    }
    if (hasProp(deviceData, 'exp_valv_ui')) {
      device.exp_valv_ui = parseNumber(deviceData.exp_valv_ui);
    }
    if (hasProp(deviceData, 'pe_ue')) {
      device.pe_ue = parseNumber(deviceData.pe_ue);
    }
    if (hasProp(deviceData, 'pc_ue')) {
      device.pc_ue = parseNumber(deviceData.pc_ue);
    }
    if (hasProp(deviceData, 'range_sp_acs_max')) {
      device.range_sp_acs_max = parseNumber(deviceData.range_sp_acs_max);
    }
    if (hasProp(deviceData, 'range_sp_acs_min')) {
      device.range_sp_acs_min = parseNumber(deviceData.range_sp_acs_min);
    }
    if (hasProp(deviceData, 'range_sp_auto_air_max')) {
      device.range_sp_auto_air_max = parseNumber(deviceData.range_sp_auto_air_max);
    }
    if (hasProp(deviceData, 'range_sp_auto_air_min')) {
      device.range_sp_auto_air_min = parseNumber(deviceData.range_sp_auto_air_min);
    }
    if (hasProp(deviceData, 'range_sp_auto_water_max')) {
      device.range_sp_auto_water_max = parseNumber(deviceData.range_sp_auto_water_max);
    }
    if (hasProp(deviceData, 'range_sp_auto_water_min')) {
      device.range_sp_auto_water_min = parseNumber(deviceData.range_sp_auto_water_min);
    }
    if (hasProp(deviceData, 'range_sp_cool_air_max')) {
      device.range_sp_cool_air_max = parseNumber(deviceData.range_sp_cool_air_max);
    }
    if (hasProp(deviceData, 'range_sp_cool_air_min')) {
      device.range_sp_cool_air_min = parseNumber(deviceData.range_sp_cool_air_min);
    }
    if (hasProp(deviceData, 'range_sp_cool_water_max')) {
      device.range_sp_cool_water_max = parseNumber(deviceData.range_sp_cool_water_max);
    }
    if (hasProp(deviceData, 'range_sp_cool_water_min')) {
      device.range_sp_cool_water_min = parseNumber(deviceData.range_sp_cool_water_min);
    }
    if (hasProp(deviceData, 'range_sp_hot_air_max')) {
      device.range_sp_hot_air_max = parseNumber(deviceData.range_sp_hot_air_max);
    }
    if (hasProp(deviceData, 'range_sp_hot_air_min')) {
      device.range_sp_hot_air_min = parseNumber(deviceData.range_sp_hot_air_min);
    }
    if (hasProp(deviceData, 'range_sp_hot_water_max')) {
      device.range_sp_hot_water_max = parseNumber(deviceData.range_sp_hot_water_max);
    }
    if (hasProp(deviceData, 'range_sp_hot_water_min')) {
      device.range_sp_hot_water_min = parseNumber(deviceData.range_sp_hot_water_min);
    }
    if (hasProp(deviceData, 'range_sp_vent_max')) {
      device.range_sp_vent_max = parseNumber(deviceData.range_sp_vent_max);
    }
    if (hasProp(deviceData, 'range_sp_vent_min')) {
      device.range_sp_vent_min = parseNumber(deviceData.range_sp_vent_min);
    }
    if (hasProp(deviceData, 'range_sp_dry_max')) {
      device.range_sp_dry_max = parseNumber(deviceData.range_sp_dry_max);
    }
    if (hasProp(deviceData, 'range_sp_dry_min')) {
      device.range_sp_dry_min = parseNumber(deviceData.range_sp_dry_min);
    }
    if (hasProp(deviceData, 'real_mode')) {
      device.real_mode = parseNumber(deviceData.real_mode);
    }
    if (hasProp(deviceData, 'setpoint_acs')) {
      device.setpoint_acs = parseNumber(deviceData.setpoint_acs);
      device.setpoint_acs = parseBetween(
        device.setpoint_acs,
        device.range_sp_acs_max,
        device.range_sp_acs_min
      );
    }
    if (hasProp(deviceData, 'setpoint_air_auto')) {
      device.setpoint_air_auto = parseNumber(deviceData.setpoint_air_auto);
      device.setpoint_air_auto = parseBetween(
        device.setpoint_air_auto,
        device.range_sp_auto_air_max,
        device.range_sp_auto_air_min
      );
    }
    if (hasProp(deviceData, 'setpoint_air_cool')) {
      device.setpoint_air_cool = parseNumber(deviceData.setpoint_air_cool);
      device.setpoint_air_cool = parseBetween(
        device.setpoint_air_cool,
        device.range_sp_cool_air_max,
        device.range_sp_cool_air_min
      );
    }
    if (hasProp(deviceData, 'setpoint_air_heat')) {
      device.setpoint_air_heat = parseNumber(deviceData.setpoint_air_heat);
      device.setpoint_air_heat = parseBetween(
        device.setpoint_air_heat,
        device.range_sp_hot_air_max,
        device.range_sp_hot_air_min
      );
    }
    if (hasProp(deviceData, 'setpoint_air_vent')) {
      device.setpoint_air_vent = parseNumber(deviceData.setpoint_air_vent);
      device.setpoint_air_vent = parseBetween(
        device.setpoint_air_vent,
        device.range_sp_vent_air_max,
        device.range_sp_vent_air_min
      );
    }
    if (hasProp(deviceData, 'setpoint_air_dry')) {
      device.setpoint_air_dry = parseNumber(deviceData.setpoint_air_dry);
      device.setpoint_air_dry = parseBetween(
        device.setpoint_air_dry,
        device.range_sp_dry_air_max,
        device.range_sp_dry_air_min
      );
    }
    if (hasProp(deviceData, 'setpoint_water_auto')) {
      device.setpoint_water_auto = parseNumber(deviceData.setpoint_water_auto);
      device.setpoint_water_auto = parseBetween(
        device.setpoint_water_auto,
        device.range_sp_auto_water_max,
        device.range_sp_auto_water_min
      );
    }
    if (hasProp(deviceData, 'setpoint_water_cool')) {
      device.setpoint_water_cool = parseNumber(deviceData.setpoint_water_cool);
      device.setpoint_water_cool = parseBetween(
        device.setpoint_water_cool,
        device.range_sp_cool_water_max,
        device.range_sp_cool_water_min
      );
    }
    if (hasProp(deviceData, 'setpoint_water_heat')) {
      device.setpoint_water_heat = parseNumber(deviceData.setpoint_water_heat);
      device.setpoint_water_heat = parseBetween(
        device.setpoint_water_heat,
        device.range_sp_hot_water_max,
        device.range_sp_hot_water_min
      );
    }
    if (hasProp(deviceData, 'setpoint_step')) {
      device.setpoint_step = parseBoolean(deviceData.setpoint_step);
    } else {
      device.setpoint_step = false;
    }
    if (hasProp(deviceData, 'slats_autoud')) {
      device.slats_autoud = parseBoolean(deviceData.slats_autoud);
    }
    if (hasProp(deviceData, 'slats_autolr')) {
      device.slats_autolr = parseBoolean(deviceData.slats_autolr);
    }
    if (hasProp(deviceData, 'slats_swingud')) {
      device.slats_swingud = parseBoolean(deviceData.slats_swingud);
    }
    if (hasProp(deviceData, 'slats_swinglr')) {
      device.slats_swinglr = parseBoolean(deviceData.slats_swinglr);
    }
    if (hasProp(deviceData, 'slats_hnum')) {
      let slats_hnum = parseNumber(deviceData.slats_hnum);
      if (slats_hnum && slats_hnum > 8) slats_hnum = 8;
      device.slats_hnum = slats_hnum;
    }
    if (hasProp(deviceData, 'slats_vnum')) {
      let slats_vnum = parseNumber(deviceData.slats_vnum);
      if (slats_vnum && slats_vnum > 8) slats_vnum = 8;
      device.slats_vnum = slats_vnum;
    }
    if (hasProp(deviceData, 'slats_horizontal_1')) {
      let slats_horizontal = parseNumber(deviceData.slats_horizontal_1);
      if (slats_horizontal < 0) slats_horizontal = 0;
      else if (slats_horizontal >= 9) slats_horizontal = 9;
      device.slats_horizontal_1 = slats_horizontal;
    }
    if (hasProp(deviceData, 'slats_vertical_1')) {
      let slats_vertical = parseNumber(deviceData.slats_vertical_1);
      if (slats_vertical < 0) slats_vertical = 0;
      else if (slats_vertical >= 9) slats_vertical = 9;
      device.slats_vertical_1 = slats_vertical;
    }
    if (hasProp(deviceData, 'speed_available') && isArray(deviceData.speed_available)) {
      device.speed_available = deviceData.speed_available;
    }
    if (hasProp(deviceData, 'speed_state')) {
      device.speed_state = parseNumber(deviceData.speed_state);
    }
    if (hasProp(deviceData, 'stat_channel')) {
      device.stat_channel = parseNumber(deviceData.stat_channel);
    }
    if (hasProp(deviceData, 'stat_rssi')) {
      device.stat_rssi = parseNumber(deviceData.stat_rssi);
    }
    if (hasProp(deviceData, 'stat_ssid')) {
      device.stat_ssid = parseString(deviceData.stat_ssid);
    }
    if (hasProp(deviceData, 'tai_th')) {
      device.tai_th = parseBoolean(deviceData.tai_th);
    }
    if (hasProp(deviceData, 'timezoneId')) {
      device.timezoneId = parseString(deviceData.timezoneId);
    }
    if (hasProp(deviceData, 'tsensor_error')) {
      device.tsensor_error = parseBoolean(deviceData.tsensor_error);
    }
    if (hasProp(deviceData, 'units')) {
      device.units = parseNumber(deviceData.units);
    }
    if (hasProp(deviceData, 'version')) {
      device.version = parseString(deviceData.version);
    }
    if (hasProp(deviceData, 'water_input_temp')) {
      device.water_input_temp = parseNumber(deviceData.water_input_temp);
    }
    if (hasProp(deviceData, 'water_output_temp')) {
      device.water_output_temp = parseNumber(deviceData.water_output_temp);
    }
    if (hasProp(deviceData, 'work_temp')) {
      device.work_temp = parseNumber(deviceData.work_temp);
    }
    if (hasProp(deviceData, 'work_temp_selec_sensor')) {
      const sensor = parseNumber(deviceData.work_temp_selec_sensor);
      switch (sensor) {
        case TEMP_SENSOR.INDOOR:
          device.work_temp_selec_sensor = 'IU';
          break;
        case TEMP_SENSOR.THERMOSTAT:
          device.work_temp_selec_sensor = 'T';
          break;
        case TEMP_SENSOR.MIX:
          device.work_temp_selec_sensor = 'M';
          break;
        default:
          break;
      }
    }
    if (hasProp(deviceData, 'emerheatpresent')) {
      device.emerheatpresent = parseBoolean(deviceData.emerheatpresent);
    }
    if (hasProp(deviceData, 'emerheatstatus')) {
      device.emerheatstatus = parseBoolean(deviceData.emerheatstatus);
    }
    if (hasProp(deviceData, 't1t2on')) {
      device.t1t2on = parseBoolean(deviceData.t1t2on);
    }

    return device;
  } catch (error) {
    consoleError(`Error al parsear los datos del dispositivo ${deviceData.mac}`);
    store.dispatch('globalModule/setError', 'invalidDevice');
    return null;
  }
};

/**
 * Comprueba si los datos de un dispositivo son válidos cuando se reciben sus datos por primera vez
 *
 * @param {Object} deviceData - Datos del dispositivo del backend
 * @return {Device} - Datos formateados y validados de un dispositivo
 */
const validateDevice = deviceData => {
  const device = sanitizeDevice(deviceData);

  if (!device || !device.name || !device.icon) {
    consoleError(`Error al validar los datos del dispositivo ${deviceData.mac}`);
    store.dispatch('globalModule/setError', 'invalidDevice');
    return null;
  }

  /**
   * Un dispositivo siempre debe tener la propiedad acs_available
   * para el buen funcionamiento de la aplicación
   */
  if (!hasProp(device, 'acs_available')) {
    device.acs_available = false;
  }

  /**
   * Un dispositivo siempre debe tener la propiedad tai_th
   * para el buen funcionamiento de la aplicación
   */
  if (!hasProp(device, 'tai_th')) {
    device.tai_th = TAI.AIR;
  }

  return device;
};

export { validateDevice, sanitizeDevice };
